import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useLeadGenerationJob } from "../hooks/useLeadGenerationJob";
import {
  decideOpportunity,
  generateOpportunities,
  getProjectRoadmap,
  listOpportunities,
  planLeadResearchBulk,
  planRoadmapEventResearch,
} from "../lib/api";
import {
  angleLabel,
  isClosedLead,
  isModelLead,
  leadStateLabel,
  shortDate,
  shortModel,
  usd,
} from "../lib/leads";
import type {
  LeadGenerationRecord,
  OpportunityHypothesis,
  ProjectLeadOrigin,
  ProjectRoadmap,
  RoadmapEvent,
} from "../lib/types";
import { formatApiError } from "../lib/utils";
import { Icon } from "./Icon";
import { useToast } from "./Toast";
import { Button } from "./ui";

type PlannedQuery = { query: string; objectiveId: string; purpose: string };

function sourceLabel(event: RoadmapEvent) {
  const count = event.supported_documents || 0;
  const origins = event.origin_groups || 0;
  return `${count} document${count === 1 ? "" : "s"} · ${origins} origin group${origins === 1 ? "" : "s"}`;
}

function EventCard({
  event,
  projectId,
  onPlan,
}: {
  event: RoadmapEvent;
  projectId: string;
  onPlan: (items: PlannedQuery[]) => void;
}) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const plan = async (statusRefresh = false) => {
    setBusy(true);
    try {
      const result = await planRoadmapEventResearch(projectId, event.event_id, {
        status_refresh: statusRefresh,
      });
      const items = (result.search_items || []).map((item) => ({
        query: item.query,
        objectiveId: result.objective.objective_id,
        purpose: statusRefresh ? "status refresh" : "supporting evidence",
      }));
      onPlan(items);
      toast(`Planned ${items.length} roadmap research ${items.length === 1 ? "query" : "queries"}.`);
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="border border-outline-variant rounded bg-surface-container-lowest px-3 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-[10px] font-data-mono uppercase">
            <span className={event.classification === "announced" ? "text-tertiary" : "text-primary"}>
              {event.classification === "announced" ? "Announced plan" : "Documented event"}
            </span>
            <span className="text-outline">{event.date_label || "date not stated"}</span>
            <span className="text-outline">{event.date_precision}</span>
            {event.status && event.status !== "unknown" ? <span className="text-secondary">{event.status}</span> : null}
          </div>
          <p className="text-body-sm text-on-surface mt-1 leading-relaxed">{event.description}</p>
          <p className="text-[10px] text-on-surface-variant mt-2">{sourceLabel(event)}</p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <Button variant="link-muted" size="sm" onClick={() => setOpen((value) => !value)}>
            {open ? "Hide sources" : "Sources"}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => void plan(false)} disabled={busy}>
            Find support
          </Button>
        </div>
      </div>
      {open ? (
        <div className="mt-3 border-t border-outline-variant pt-3 space-y-3">
          {!event.supports?.length ? (
            <p className="text-body-sm text-on-surface-variant">No direct supporting passage is attached yet.</p>
          ) : (
            event.supports.map((support) => (
              <div key={support.support_id} className="border-l-2 border-primary/40 pl-3">
                <p className="text-body-sm text-on-surface leading-relaxed">{support.passage}</p>
                <div className="mt-1 text-[10px] font-data-mono text-on-surface-variant flex flex-wrap gap-x-3 gap-y-1">
                  <span>{support.relationship}</span>
                  {support.source.published_date ? <span>published {support.source.published_date}</span> : null}
                  {support.source.last_fetched_at ? <span>fetched {support.source.last_fetched_at}</span> : null}
                </div>
                {support.source.url ? (
                  <a
                    href={support.source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-primary text-xs hover:underline truncate mt-1"
                  >
                    {support.source.title || support.source.url}
                  </a>
                ) : null}
              </div>
            ))
          )}
          <Button variant="link-muted" size="sm" onClick={() => void plan(true)} disabled={busy}>
            Find a status update or contradiction
          </Button>
        </div>
      ) : null}
    </article>
  );
}

function Chip({ children, tone = "neutral", title }: { children: ReactNode; tone?: string; title?: string }) {
  const tones: Record<string, string> = {
    neutral: "border-outline-variant text-on-surface-variant",
    tertiary: "border-tertiary/40 text-tertiary bg-tertiary/5",
    primary: "border-primary/40 text-primary bg-primary/5",
    secondary: "border-secondary/40 text-secondary bg-secondary/5",
    error: "border-error/40 text-error bg-error/5",
  };
  return (
    <span
      title={title}
      className={`inline-flex items-center px-1.5 py-0.5 border rounded text-[9px] uppercase tracking-wide font-data-mono ${tones[tone] || tones.neutral}`}
    >
      {children}
    </span>
  );
}

function ResearchLine({
  lead,
  onLink,
  onOpenProject,
  busy,
}: {
  lead: OpportunityHypothesis;
  onLink: (projectId: string) => void;
  onOpenProject?: (projectId: string) => void;
  busy: boolean;
}) {
  const research = lead.research;
  if (!research || research.state === "not_planned") {
    return <p className="text-[11px] text-on-surface-variant">Not researched yet.</p>;
  }
  if (research.state === "planned") {
    const count = research.queries.length;
    return (
      <p className="text-[11px] text-on-surface-variant">
        Queued for Phase 1: {count} {count === 1 ? "query" : "queries"}, first run. Select them on the{" "}
        <Link to="/search?leads=1" className="text-primary hover:underline">
          Search page
        </Link>
        .
      </p>
    );
  }
  const found = research.found_projects || [];
  const linkedId = lead.matched_project_id;
  return (
    <div className="space-y-1">
      <p className="text-[11px] text-on-surface-variant">
        Researched {research.attempts}× {research.last_run_at ? `· last ${shortDate(research.last_run_at)}` : ""} ·{" "}
        {research.search_hits} search result{research.search_hits === 1 ? "" : "s"} · {research.pages_found} page
        {research.pages_found === 1 ? "" : "s"} kept
        {research.pages_found && !found.length ? " · no project found in them yet (extract and reconcile to check)" : ""}
      </p>
      {found.length ? (
        <ul className="space-y-1">
          {found.map((project) => (
            <li key={project.project_id} className="flex flex-wrap items-center gap-2 text-[11px]">
              <Icon name="subdirectory_arrow_right" className="text-sm text-tertiary" />
              {onOpenProject ? (
                <button
                  type="button"
                  className="text-primary hover:underline text-left"
                  onClick={() => onOpenProject(project.project_id)}
                >
                  {project.project_name}
                </button>
              ) : (
                <span className="text-on-surface">{project.project_name}</span>
              )}
              <span className="text-outline font-data-mono">
                {project.documents} doc{project.documents === 1 ? "" : "s"}
              </span>
              {linkedId === project.project_id ? (
                <Chip tone="primary">linked</Chip>
              ) : lead.workflow_state !== "completed" ? (
                <Button variant="link-muted" disabled={busy} onClick={() => onLink(project.project_id)}>
                  This is the lead
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function LeadCard({
  lead,
  sourceText,
  onChanged,
  onOpenProject,
}: {
  lead: OpportunityHypothesis;
  sourceText: string;
  onChanged: () => void;
  onOpenProject?: (projectId: string) => void;
}) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const closed = isClosedLead(lead);

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      toast(label);
      onChanged();
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setBusy(false);
    }
  };

  const queue = () =>
    run("Lead sent to Search. Select its queries on the Search page.", async () => {
      await planLeadResearchBulk({ hypothesis_ids: [lead.hypothesis_id] });
    });
  const DONE: Record<string, string> = {
    dismiss: "dismissed",
    defer: "deferred",
    reopen: "reopened",
    mark_distinct: "marked distinct",
  };
  const decide = (action: "dismiss" | "defer" | "reopen" | "mark_distinct") =>
    run(`Lead ${DONE[action]}.`, () => decideOpportunity(lead.hypothesis_id, { action }));
  const link = (projectId: string) =>
    run("Lead linked to that project; both now list each other as related.", () =>
      decideOpportunity(lead.hypothesis_id, { action: "link_existing", matched_project_id: projectId }),
    );

  const stateTone =
    lead.workflow_state === "completed" ? "primary" : closed ? "error" : lead.workflow_state === "proposed" ? "tertiary" : "secondary";
  const queries = lead.search_queries || [];
  const suppliers = lead.likely_suppliers || [];
  const confirm = lead.confirmation_tests || [];
  const refute = lead.counterevidence_tests || [];
  const researchable = ["proposed", "eligible", "queued", "cooldown"].includes(lead.workflow_state);

  return (
    <article className={`border rounded px-3 py-3 ${closed ? "border-outline-variant opacity-70" : "border-tertiary/30 bg-tertiary/5"}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-body-sm font-semibold text-on-surface">{lead.proposed_work}</p>
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            <Chip tone={stateTone}>{leadStateLabel(lead)}</Chip>
            {lead.angle ? <Chip>{angleLabel(lead.angle)}</Chip> : null}
            {lead.horizon?.label && lead.horizon.label !== "unknown" ? (
              <Chip title="When the work would most likely start">{lead.horizon.label}</Chip>
            ) : null}
            {isModelLead(lead) ? (
              <Chip title="How directly the premise implies this work">{lead.research_priority} confidence</Chip>
            ) : (
              <Chip title="Generated by the keyword rule catalogue">rule</Chip>
            )}
          </div>
          {sourceText ? <p className="text-[10px] font-data-mono text-outline mt-1">{sourceText}</p> : null}
        </div>
        <div className="flex flex-wrap gap-1 shrink-0">
          {researchable ? (
            <Button variant="secondary" size="sm" onClick={() => void queue()} disabled={busy}>
              {lead.research && lead.research.state !== "not_planned" ? "Queue again" : "Queue research"}
            </Button>
          ) : null}
          {closed || lead.workflow_state === "deferred" ? (
            <Button variant="link-muted" size="sm" onClick={() => void decide("reopen")} disabled={busy}>
              Reopen
            </Button>
          ) : lead.workflow_state !== "completed" ? (
            <>
              <Button variant="link-muted" size="sm" onClick={() => void decide("defer")} disabled={busy}>
                Defer
              </Button>
              <Button variant="link-muted" size="sm" onClick={() => void decide("dismiss")} disabled={busy}>
                Dismiss
              </Button>
            </>
          ) : null}
        </div>
      </div>
      {lead.rationale ? (
        <p className="text-body-sm text-on-surface-variant mt-2 leading-relaxed">{lead.rationale}</p>
      ) : null}
      <dl className="mt-2 space-y-1 text-[11px]">
        {suppliers.length ? (
          <div className="flex gap-2">
            <dt className="text-outline shrink-0 w-24">Likely suppliers</dt>
            <dd className="text-on-surface">{suppliers.join(" · ")}</dd>
          </div>
        ) : null}
        {queries.length ? (
          <div className="flex gap-2">
            <dt className="text-outline shrink-0 w-24">Keywords</dt>
            <dd className="flex flex-wrap gap-1">
              {queries.map((query) => (
                <span key={query} className="font-data-mono text-on-surface bg-surface-container px-1.5 py-0.5 rounded">
                  {query}
                </span>
              ))}
            </dd>
          </div>
        ) : null}
        {confirm.length ? (
          <div className="flex gap-2">
            <dt className="text-outline shrink-0 w-24">Would confirm</dt>
            <dd className="text-on-surface-variant">{confirm.join(" ")}</dd>
          </div>
        ) : null}
        {refute.length ? (
          <div className="flex gap-2">
            <dt className="text-outline shrink-0 w-24">Could refute</dt>
            <dd className="text-on-surface-variant">{refute.join(" ")}</dd>
          </div>
        ) : null}
      </dl>
      <div className="mt-2 border-t border-outline-variant/50 pt-2">
        <ResearchLine lead={lead} onLink={(id) => void link(id)} onOpenProject={onOpenProject} busy={busy} />
      </div>
    </article>
  );
}

const OPEN_ORDER: Record<string, number> = {
  proposed: 0,
  eligible: 1,
  queued: 1,
  running: 1,
  cooldown: 2,
  completed: 3,
  exhausted: 4,
  deferred: 5,
  dismissed: 6,
};

export function RoadmapDiscoveryPanel({
  projectId,
  targetCompanyId,
  refreshKey = 0,
  onOpenProject,
  onLeadsChanged,
}: {
  projectId: string;
  targetCompanyId?: string;
  /** Bumped by the owning page after bulk lead work on this project. */
  refreshKey?: number;
  onOpenProject?: (projectId: string) => void;
  onLeadsChanged?: () => void;
}) {
  const { toast } = useToast();
  const [roadmap, setRoadmap] = useState<ProjectRoadmap | null>(null);
  const [hypotheses, setHypotheses] = useState<OpportunityHypothesis[]>([]);
  const [generation, setGeneration] = useState<LeadGenerationRecord | null>(null);
  const [lastAttempt, setLastAttempt] = useState<LeadGenerationRecord | null>(null);
  const [origin, setOrigin] = useState<ProjectLeadOrigin | null>(null);
  const [planned, setPlanned] = useState<PlannedQuery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const [sending, setSending] = useState(false);
  const [showClosed, setShowClosed] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const [timeline, opportunities] = await Promise.all([
        getProjectRoadmap(projectId),
        listOpportunities({ projectId, includeDismissed: true, includeResearch: true }),
      ]);
      setRoadmap(timeline);
      setHypotheses(opportunities.hypotheses || []);
      setGeneration(opportunities.generation || null);
      setLastAttempt(opportunities.last_attempt || null);
      setOrigin(opportunities.origin || null);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const job = useLeadGenerationJob(`orion1.leads.project.${projectId}`, (state) => {
    const result = state.progress?.results?.[0];
    if (state.status !== "ok") {
      toast(state.error || `Lead generation ended: ${state.status}`, true);
    } else if (result?.status === "generated") {
      toast(`Generated ${result.leads ?? 0} possible next projects (${usd(state.progress?.cost_usd, 3)}).`);
    } else if (result?.status === "unchanged") {
      toast("Leads are already current for these roadmap events. Use Regenerate for fresh ideas.");
    } else if (result?.status === "failed") {
      toast(result.error || "Lead generation failed", true);
    } else if (result?.reason === "found_via_lead") {
      toast("This project was found via another project's lead research, so no leads are generated for it.");
    } else if (result?.reason === "no_spend_ledger") {
      toast("This bank has no spend ledger yet: run Phase 1 for it once.", true);
    } else if (result) {
      toast(`Lead generation: ${result.reason || result.status}`, true);
    }
    void load();
    onLeadsChanged?.();
  });

  const addPlanned = (items: PlannedQuery[]) => {
    setPlanned((current) => {
      const next = [...current];
      for (const item of items) {
        if (!next.some((saved) => saved.objectiveId === item.objectiveId && saved.query === item.query)) {
          next.push(item);
        }
      }
      return next;
    });
  };

  const generate = async (force: boolean) => {
    setStarting(true);
    try {
      const result = await generateOpportunities({
        project_id: projectId,
        target_company_id: targetCompanyId || "",
        force,
      });
      if (result.run_id) {
        job.start(result.run_id);
      } else {
        await load();
        toast(result.message || `Generated ${result.hypotheses?.length || 0} rule-based leads.`);
      }
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setStarting(false);
    }
  };

  const sendToSearch = async () => {
    setSending(true);
    try {
      const result = await planLeadResearchBulk({ project_ids: [projectId] });
      toast(
        result.planned
          ? `Sent ${result.planned} lead${result.planned === 1 ? "" : "s"} to Search (${result.queries} queries). Select them on the Search page.`
          : "No open leads to send.",
        !result.planned,
      );
      await load();
      onLeadsChanged?.();
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setSending(false);
    }
  };

  const eventsById = useMemo(() => {
    const map: Record<string, RoadmapEvent> = {};
    for (const event of roadmap?.events || []) map[event.event_id] = event;
    return map;
  }, [roadmap]);

  const sourceText = useCallback(
    (lead: OpportunityHypothesis) => {
      if (lead.source_event_id) {
        const event = eventsById[lead.source_event_id];
        return event ? `From roadmap event: ${event.description}` : "From a roadmap event no longer on this project";
      }
      if (isModelLead(lead)) return "From the project record (no structured roadmap events)";
      return "From the keyword rule catalogue";
    },
    [eventsById],
  );

  const sorted = useMemo(
    () =>
      [...hypotheses].sort((a, b) => {
        const byState = (OPEN_ORDER[a.workflow_state] ?? 9) - (OPEN_ORDER[b.workflow_state] ?? 9);
        if (byState) return byState;
        return (a.created_at || "").localeCompare(b.created_at || "");
      }),
    [hypotheses],
  );
  const open = sorted.filter((lead) => !isClosedLead(lead));
  const closed = sorted.filter((lead) => isClosedLead(lead));
  const researchable = open.filter((lead) => ["proposed", "eligible", "queued", "cooldown"].includes(lead.workflow_state));
  const eventCount = roadmap?.events.length ?? 0;
  const foundViaLead = Boolean(origin?.found_via_lead);
  const busy = starting || job.running || foundViaLead;
  const failedLast = lastAttempt && lastAttempt.status === "failed" && lastAttempt.generation_id !== generation?.generation_id;

  return (
    <section className="panel-border bg-surface-container-low p-4 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[10px] uppercase tracking-widest text-on-surface-variant font-data-label flex items-center gap-2">
            <Icon name="timeline" className="text-base" />
            Evidence roadmap
          </h3>
          <p className="text-body-sm text-on-surface-variant mt-1">
            Documented history and source-announced plans only. Internal possible next projects are kept below.
          </p>
        </div>
        <Button variant="link-muted" size="sm" onClick={() => void load()} disabled={loading}>
          Refresh
        </Button>
      </div>
      {error ? <p className="text-body-sm text-error">{error}</p> : null}
      {loading && !roadmap ? <p className="text-body-sm text-on-surface-variant">Loading source-backed timeline…</p> : null}
      {roadmap ? (
        <>
          <p className="text-[10px] font-data-mono text-outline">
            {roadmap.coverage.documents} documents · {roadmap.coverage.origin_groups} origin groups · {roadmap.coverage.supported_events}/{roadmap.coverage.events} milestones supported
            {roadmap.coverage.contradictions ? ` · ${roadmap.coverage.contradictions} contradiction(s)` : ""}
          </p>
          {!roadmap.events.length ? (
            <p className="text-body-sm text-on-surface-variant">
              No accepted structured milestones yet. Existing PDR roadmap text remains available as legacy content until supported passages are extracted.
            </p>
          ) : (
            <div className="space-y-2">
              {roadmap.events.map((event) => (
                <EventCard key={event.event_id} event={event} projectId={projectId} onPlan={addPlanned} />
              ))}
            </div>
          )}
        </>
      ) : null}

      <div className="border-t border-outline-variant pt-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h4 className="text-[10px] uppercase tracking-widest text-tertiary font-data-label">Possible next projects</h4>
            <p className="text-body-sm text-on-surface-variant mt-1">
              Internal research leads, not confirmed projects or factual roadmap events.{" "}
              {eventCount
                ? `3 per roadmap event (${eventCount} event${eventCount === 1 ? "" : "s"}).`
                : "No roadmap events yet, so leads come from the project record."}
            </p>
            <p className="text-[10px] font-data-mono text-outline mt-1">
              {generation
                ? `Generated ${shortDate(generation.created_at)} by ${shortModel(generation.model)} · ${generation.lead_count} leads${generation.error ? ` · ${generation.error}` : ""}`
                : "No model leads generated yet"}
              {failedLast ? ` · last attempt failed: ${lastAttempt?.error}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <Button variant="secondary" size="sm" onClick={() => void generate(false)} disabled={busy}>
              {job.running ? "Generating…" : "Generate leads"}
            </Button>
            {generation ? (
              <Button
                variant="link-muted"
                onClick={() => void generate(true)}
                disabled={busy}
                title="Ask the model again even though the roadmap has not changed; untouched leads are replaced"
              >
                Regenerate
              </Button>
            ) : null}
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => void sendToSearch()}
              disabled={sending || busy || !researchable.length}
              title="Plan every open lead's keywords as Phase 1 queries"
            >
              Send {researchable.length || ""} to Search
            </Button>
          </div>
        </div>
        {foundViaLead ? (
          <div className="border border-tertiary/30 bg-tertiary/5 px-3 py-2 text-body-sm text-on-surface-variant">
            <Icon name="call_split" className="text-base text-tertiary align-middle mr-1" />
            Found via lead research
            {origin?.origin_lead || origin?.origin_project_id ? (
              <>
                {" ("}
                {origin?.origin_lead ? <>lead &ldquo;{origin.origin_lead}&rdquo;</> : null}
                {origin?.origin_project_id ? (
                  <>
                    {origin?.origin_lead ? " from " : "from "}
                    {onOpenProject ? (
                      <button
                        type="button"
                        className="text-primary hover:underline"
                        onClick={() => onOpenProject(origin.origin_project_id)}
                      >
                        {origin.origin_project_name || "its project"}
                      </button>
                    ) : (
                      origin.origin_project_name || "its project"
                    )}
                  </>
                ) : null}
                {")"}
              </>
            ) : null}
            . Leads are not generated for projects found this way, so lead research does not start a
            second round.
          </div>
        ) : null}
        {job.running ? (
          <p className="text-[11px] text-on-surface-variant flex items-center gap-2">
            <span className="orion-spinner" aria-hidden />
            Asking the model for possible next projects… this takes about 10–40 seconds.
          </p>
        ) : null}
        <div className="space-y-2">
          {!open.length && !loading ? (
            <p className="text-body-sm text-on-surface-variant">No open leads. Generate leads to get possible next projects.</p>
          ) : null}
          {open.map((lead) => (
            <LeadCard
              key={lead.hypothesis_id}
              lead={lead}
              sourceText={sourceText(lead)}
              onChanged={() => {
                void load();
                onLeadsChanged?.();
              }}
              onOpenProject={onOpenProject}
            />
          ))}
        </div>
        {closed.length ? (
          <div>
            <Button variant="link-muted" onClick={() => setShowClosed((value) => !value)}>
              {showClosed ? "Hide" : "Show"} dismissed and superseded ({closed.length})
            </Button>
            {showClosed ? (
              <div className="space-y-2 mt-2">
                {closed.map((lead) => (
                  <LeadCard
                    key={lead.hypothesis_id}
                    lead={lead}
                    sourceText={sourceText(lead)}
                    onChanged={() => {
                      void load();
                      onLeadsChanged?.();
                    }}
                    onOpenProject={onOpenProject}
                  />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {planned.length ? (
        <div className="border-t border-outline-variant pt-4">
          <h4 className="text-[10px] uppercase tracking-widest text-primary font-data-label">Planned research queries</h4>
          <p className="text-[10px] text-on-surface-variant mt-1">
            Planned, not run. Select them in the Lead research list on the Search page.
          </p>
          <ul className="mt-2 space-y-1">
            {planned.map((item) => (
              <li key={`${item.objectiveId}-${item.query}`} className="text-xs font-data-mono text-on-surface break-words">
                <span className="text-outline">{item.purpose}: </span>{item.query}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
